/**
 * `<RepeaterInput>` — list of nested mini-form items, one per row.
 *
 * React-side counterpart of `Arqel\FieldsAdvanced\Types\RepeaterField`
 * (FIELDS-ADV-013). Reads the following props verbatim from the
 * PHP-emitted schema:
 *
 *   - `schema`       : array<{ name, type, label?, options?, ... }>
 *                      Nested sub-form schema. Each entry describes a
 *                      single leaf input rendered inline per item.
 *   - `minItems`     : ?number  — floor for the row count.
 *   - `maxItems`     : ?number  — ceiling for the row count.
 *   - `reorderable`  : boolean  — whether the drag handle and up/down
 *                                 move buttons show.
 *   - `collapsible`  : boolean  — whether the collapse toggle shows.
 *   - `cloneable`    : boolean  — whether the clone button shows.
 *   - `itemLabel`    : ?string  — `{{key}}`-templated header per row.
 *   - `relationship` : ?string  — read defensively but unused
 *                                 client-side (PHP-side hydration/
 *                                 persistence concern).
 *
 * ## Reordering (FIELDS-ADV-013 — full)
 *
 * Two complementary mechanisms ship together:
 *
 *   - `@dnd-kit/sortable` drag-drop reorder via a dedicated handle on
 *     the left of each card. The handle is the only drag listener so
 *     sub-fields stay interactive (clicking an input never starts a
 *     drag). `PointerSensor` uses a 5px activation distance to avoid
 *     hijacking accidental clicks; `KeyboardSensor` makes the handle
 *     fully keyboard-operable (Space to grab, Arrow keys to move,
 *     Space to drop, Esc to cancel). dnd-kit emits screen-reader
 *     announcements out of the box.
 *   - "Move up" / "Move down" icon buttons remain as a redundant
 *     a11y fallback for keyboard-only users who don't want the grab/
 *     move/drop interaction (and for assistive tech that doesn't play
 *     nicely with DnD widgets).
 *
 * When `reorderable=false`, both the drag handle and the up/down
 * buttons are hidden / disabled.
 */

import { useArqelTranslations } from '@arqel-dev/react/utils';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useId, useState } from 'react';
import type { FieldRendererProps } from '../shared/types.js';

interface RepeaterProps {
  schema: SubFieldSchema[];
  minItems: number | null;
  maxItems: number | null;
  reorderable: boolean;
  collapsible: boolean;
  cloneable: boolean;
  itemLabel: string | null;
}

type SubFieldOptions =
  | ReadonlyArray<{ value: string | number; label: string }>
  | Record<string, string>;

interface SubFieldSchema {
  name: string;
  type: string;
  label?: string;
  // Options can arrive flat (`options`) for back-compat or, since #221,
  // nested under `props.options` — the canonical FieldSchema shape
  // produced by `FieldSchemaSerializer` and read by the top-level
  // SelectInput. `subFieldOptions()` reconciles both.
  options?: SubFieldOptions;
  props?: { options?: SubFieldOptions } & Record<string, unknown>;
  placeholder?: string;
}

/**
 * Resolve a nested sub-field's select options from the canonical
 * `props.options` shape (FieldSchemaSerializer, #221), falling back to a
 * flat `options` key for back-compat with hand-built schemas.
 */
function subFieldOptions(field: SubFieldSchema): SubFieldOptions | undefined {
  return field.props?.options ?? field.options;
}

interface InternalItem extends Record<string, unknown> {
  __id: string;
}

const SUPPORTED_TYPES = new Set([
  'text',
  'string',
  'number',
  'select',
  'textarea',
  'boolean',
  'checkbox',
]);

const inputClasses =
  'h-9 w-full rounded-sm border border-[var(--input)] ' +
  'bg-background px-3 text-sm text-foreground ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-invalid:border-destructive';

const textareaClasses =
  'w-full rounded-sm border border-[var(--input)] ' +
  'bg-background px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const buttonClasses =
  'inline-flex h-8 items-center justify-center rounded-sm ' +
  'border border-[var(--input)] bg-background ' +
  'px-3 text-sm text-foreground ' +
  'hover:bg-muted ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const iconButtonClasses =
  'inline-flex h-8 w-8 items-center justify-center rounded-sm ' +
  'border border-[var(--input)] bg-background ' +
  'text-sm text-foreground ' +
  'hover:bg-muted ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const dragHandleClasses =
  'inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-sm ' +
  'border border-[var(--input)] bg-background ' +
  'text-sm text-muted-foreground ' +
  'hover:bg-muted ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'active:cursor-grabbing';

function generateId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  return `rep-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function readProps(raw: unknown): RepeaterProps {
  const p = (raw ?? {}) as Partial<Record<keyof RepeaterProps, unknown>>;
  const schema = Array.isArray(p.schema)
    ? (p.schema.filter(
        (entry): entry is SubFieldSchema =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as { name?: unknown }).name === 'string' &&
          typeof (entry as { type?: unknown }).type === 'string',
      ) as SubFieldSchema[])
    : [];
  return {
    schema,
    minItems:
      typeof p.minItems === 'number' && Number.isFinite(p.minItems)
        ? Math.max(0, Math.floor(p.minItems))
        : null,
    maxItems:
      typeof p.maxItems === 'number' && Number.isFinite(p.maxItems)
        ? Math.max(1, Math.floor(p.maxItems))
        : null,
    reorderable: typeof p.reorderable === 'boolean' ? p.reorderable : true,
    collapsible: typeof p.collapsible === 'boolean' ? p.collapsible : false,
    cloneable: typeof p.cloneable === 'boolean' ? p.cloneable : true,
    itemLabel: typeof p.itemLabel === 'string' ? p.itemLabel : null,
  };
}

function defaultValueFor(type: string): unknown {
  switch (type) {
    case 'boolean':
    case 'checkbox':
      return false;
    case 'number':
      return null;
    default:
      return '';
  }
}

function emptyItem(schema: SubFieldSchema[]): InternalItem {
  const item: InternalItem = { __id: generateId() };
  for (const sub of schema) {
    item[sub.name] = defaultValueFor(sub.type);
  }
  return item;
}

function hydrate(value: unknown, schema: SubFieldSchema[]): InternalItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null,
    )
    .map((entry) => {
      const out: InternalItem = { __id: generateId() };
      for (const sub of schema) {
        out[sub.name] =
          sub.name in entry
            ? (entry as Record<string, unknown>)[sub.name]
            : defaultValueFor(sub.type);
      }
      // Preserve unknown keys so schema/persisted-shape drift doesn't
      // silently drop user data.
      for (const [k, v] of Object.entries(entry)) {
        if (!(k in out)) out[k] = v;
      }
      return out;
    });
}

function emit(items: InternalItem[]): Array<Record<string, unknown>> {
  return items.map(({ __id: _id, ...rest }) => rest);
}

function resolveLabel(template: string | null, item: InternalItem, fallback: string): string {
  if (!template) return fallback;
  return template.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_match, key: string) => {
    const v = item[key];
    if (v === undefined || v === null) return '';
    return String(v);
  });
}

interface SubFieldInputProps {
  field: SubFieldSchema;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean | undefined;
  inputId: string;
}

function SubFieldInput({ field, value, onChange, disabled, inputId }: SubFieldInputProps) {
  const t = useArqelTranslations();
  const type = field.type;

  if (type === 'textarea') {
    return (
      <textarea
        id={inputId}
        className={textareaClasses}
        value={typeof value === 'string' ? value : String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={3}
        placeholder={field.placeholder}
      />
    );
  }

  if (type === 'boolean' || type === 'checkbox') {
    return (
      <input
        id={inputId}
        type="checkbox"
        className="h-4 w-4"
        checked={value === true}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
    );
  }

  if (type === 'number') {
    return (
      <input
        id={inputId}
        type="number"
        className={inputClasses}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
        disabled={disabled}
        placeholder={field.placeholder}
      />
    );
  }

  if (type === 'select') {
    const rawOptions = subFieldOptions(field);
    const options = (() => {
      if (Array.isArray(rawOptions)) return rawOptions;
      if (rawOptions && typeof rawOptions === 'object') {
        return Object.entries(rawOptions as Record<string, string>).map(([v, label]) => ({
          value: v,
          label,
        }));
      }
      return [] as ReadonlyArray<{ value: string | number; label: string }>;
    })();
    return (
      <select
        id={inputId}
        className={inputClasses}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  // text / string / unknown fallback
  const note = SUPPORTED_TYPES.has(type) ? null : (
    <p className="mt-1 text-xs text-muted-foreground">
      {t('arqel.fields_advanced.unsupported_field_type', 'type :type not yet supported', { type })}
    </p>
  );

  return (
    <div>
      <input
        id={inputId}
        type="text"
        className={inputClasses}
        value={typeof value === 'string' ? value : String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={field.placeholder}
      />
      {note}
    </div>
  );
}

interface SortableRowProps {
  id: string;
  reorderable: boolean;
  index: number;
  children: (handleProps: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
  }) => React.ReactNode;
}

function SortableRow({ id, reorderable, index, children }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !reorderable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: 'relative',
  };

  return (
    <li ref={setNodeRef} style={style} data-item-index={index}>
      {children({ listeners, attributes })}
    </li>
  );
}

export function RepeaterInput({
  field,
  value,
  onChange,
  errors,
  disabled,
  inputId,
  describedBy,
}: FieldRendererProps) {
  const t = useArqelTranslations();
  const props = readProps((field as { props?: unknown }).props);
  const hasError = errors !== undefined && errors.length > 0;
  const fallbackId = useId();
  const baseId = inputId ?? fallbackId;

  const [items, setItems] = useState<InternalItem[]>(() => hydrate(value, props.schema));
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateItems = (next: InternalItem[]) => {
    setItems(next);
    onChange(emit(next));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.__id === active.id);
    const newIndex = items.findIndex((it) => it.__id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    updateItems(arrayMove(items, oldIndex, newIndex));
  };

  const updateItemField = (id: string, name: string, next: unknown) => {
    updateItems(items.map((it) => (it.__id === id ? { ...it, [name]: next } : it)));
  };

  const moveItem = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    updateItems(next);
  };

  const removeItem = (id: string) => {
    if (props.minItems !== null && items.length <= props.minItems) return;
    updateItems(items.filter((it) => it.__id !== id));
  };

  const cloneItem = (id: string) => {
    if (props.maxItems !== null && items.length >= props.maxItems) return;
    const idx = items.findIndex((it) => it.__id === id);
    if (idx < 0) return;
    const source = items[idx];
    if (!source) return;
    const copy: InternalItem = { ...source, __id: generateId() };
    const next = items.slice();
    next.splice(idx + 1, 0, copy);
    updateItems(next);
  };

  const addItem = () => {
    if (props.maxItems !== null && items.length >= props.maxItems) return;
    updateItems([...items, emptyItem(props.schema)]);
  };

  const toggleCollapsed = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const atMax = props.maxItems !== null && items.length >= props.maxItems;
  const atMin = props.minItems !== null && items.length <= props.minItems;

  const legendId = `${baseId}-legend`;

  return (
    <fieldset
      id={baseId}
      className="space-y-3"
      disabled={disabled}
      aria-describedby={describedBy}
      aria-invalid={hasError || undefined}
    >
      {field.label ? (
        <legend id={legendId} className="text-sm font-medium text-foreground">
          {field.label}
        </legend>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.__id)} strategy={verticalListSortingStrategy}>
          <ol className="list-none space-y-2 pl-0">
            {items.map((item, index) => {
              const itemTitleId = `${baseId}-item-${index}-title`;
              const fallbackLabel = `Item ${index + 1}`;
              const labelText = resolveLabel(props.itemLabel, item, fallbackLabel) || fallbackLabel;
              const isCollapsed = props.collapsible && collapsedIds.has(item.__id);
              return (
                <SortableRow
                  key={item.__id}
                  id={item.__id}
                  reorderable={props.reorderable}
                  index={index}
                >
                  {({ listeners, attributes }) => (
                    <article
                      aria-labelledby={itemTitleId}
                      className="rounded-sm border border-[var(--input)] bg-background p-3"
                    >
                      <header className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {props.reorderable ? (
                            <button
                              type="button"
                              className={dragHandleClasses}
                              aria-label={t(
                                'arqel.fields_advanced.repeater_drag',
                                `Drag to reorder item ${index + 1}`,
                                { number: index + 1 },
                              )}
                              data-testid={`repeater-drag-handle-${index}`}
                              {...attributes}
                              {...listeners}
                            >
                              ≡
                            </button>
                          ) : null}
                          <h3 id={itemTitleId} className="text-sm font-medium text-foreground">
                            {labelText}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1">
                          {props.collapsible ? (
                            <button
                              type="button"
                              className={iconButtonClasses}
                              aria-label={
                                isCollapsed
                                  ? t(
                                      'arqel.fields_advanced.repeater_expand',
                                      `Expand item ${index + 1}`,
                                      { number: index + 1 },
                                    )
                                  : t(
                                      'arqel.fields_advanced.repeater_collapse',
                                      `Collapse item ${index + 1}`,
                                      { number: index + 1 },
                                    )
                              }
                              aria-expanded={!isCollapsed}
                              onClick={() => toggleCollapsed(item.__id)}
                            >
                              {isCollapsed ? '▸' : '▾'}
                            </button>
                          ) : null}
                          {props.reorderable ? (
                            <>
                              <button
                                type="button"
                                className={iconButtonClasses}
                                aria-label={t('arqel.fields_advanced.repeater_move_up', 'Move up')}
                                disabled={index === 0}
                                onClick={() => moveItem(index, -1)}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className={iconButtonClasses}
                                aria-label={t(
                                  'arqel.fields_advanced.repeater_move_down',
                                  'Move down',
                                )}
                                disabled={index === items.length - 1}
                                onClick={() => moveItem(index, 1)}
                              >
                                ↓
                              </button>
                            </>
                          ) : null}
                          {props.cloneable ? (
                            <button
                              type="button"
                              className={iconButtonClasses}
                              aria-label={t(
                                'arqel.fields_advanced.repeater_clone',
                                `Clone item ${index + 1}`,
                                { number: index + 1 },
                              )}
                              disabled={atMax}
                              onClick={() => cloneItem(item.__id)}
                            >
                              ⎘
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={iconButtonClasses}
                            aria-label={t(
                              'arqel.fields_advanced.repeater_remove',
                              `Remove item ${index + 1}`,
                              { number: index + 1 },
                            )}
                            disabled={atMin}
                            onClick={() => removeItem(item.__id)}
                          >
                            ×
                          </button>
                        </div>
                      </header>

                      {!isCollapsed ? (
                        <div className="mt-3 grid grid-cols-1 gap-3">
                          {props.schema.map((sub) => {
                            const subId = `${baseId}-item-${index}-${sub.name}`;
                            const subLabel = sub.label ?? sub.name;
                            return (
                              <div key={sub.name} className="grid gap-1">
                                <label
                                  htmlFor={subId}
                                  className="text-xs font-medium text-muted-foreground"
                                >
                                  {subLabel}
                                </label>
                                <SubFieldInput
                                  field={sub}
                                  value={item[sub.name]}
                                  onChange={(next) => updateItemField(item.__id, sub.name, next)}
                                  disabled={disabled}
                                  inputId={subId}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </article>
                  )}
                </SortableRow>
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        className={buttonClasses}
        onClick={addItem}
        disabled={disabled || atMax}
        aria-label={t('arqel.fields_advanced.repeater_add_item', 'Add item')}
      >
        {t('arqel.fields_advanced.repeater_add_item_label', '+ Add item')}
      </button>
    </fieldset>
  );
}
